import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../api';
import { useGame } from '../context/GameContext';

function fmtResource(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const SECTOR_ICONS = {
  core: '🌟', asteroid: '☄', nebula: '🌫', ice: '❄', volcanic: '🌋',
  barren: '🏜', lush: '🌿', gas_giant: '💨', dark: '🕳',
};

function SectorPanel() {
  const { refreshInventory } = useGame();
  const [sectors, setSectors] = useState([]);
  const [undiscovered, setUndiscovered] = useState([]);
  const [selectedSector, setSelectedSector] = useState(null);
  const [sectorDetail, setSectorDetail] = useState(null);
  const [sectorInv, setSectorInv] = useState(null);
  const [drones, setDrones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const pollRef = useRef(null);

  const loadSectors = useCallback(async () => {
    try {
      setLoading(true);
      const [secRes, undRes, droneRes] = await Promise.all([
        api.getSectors(),
        api.getUndiscoveredSectors(),
        api.getFleet(),
      ]);
      setSectors(secRes.data.sectors || []);
      setUndiscovered(undRes.data.sectors || []);
      setDrones((droneRes.data.drones || []).filter(d => !d.is_deployed));
    } catch (err) {
      console.error('Load sectors error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSectors(); }, [loadSectors]);

  const loadDetail = useCallback(async (id) => {
    try {
      const { data } = await api.getSector(id);
      setSectorDetail(data.sector);
      setSelectedSector(id);
      // Use the fresh inventory returned by the sector detail endpoint
      setSectorInv(data.sector.inventory || null);
    } catch (err) {
      console.error('Load sector detail error:', err);
    }
  }, []);

  // Poll sector inventory every 15s when a sector is selected (drones mine in background)
  useEffect(() => {
    if (!selectedSector) return;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.getSector(selectedSector);
        setSectorDetail(data.sector);
        setSectorInv(data.sector.inventory || null);
      } catch (_) {}
    }, 15000);
    return () => clearInterval(pollRef.current);
  }, [selectedSector]);

  const handleClaim = async (id) => {
    try {
      await api.claimSector(id);
      setActionMsg('Sector claimed!');
      await loadSectors();
      loadDetail(id);
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Claim failed');
    }
  };

  const handleScout = async (id) => {
    try {
      await api.scoutSector(id);
      setActionMsg('Sector discovered!');
      await loadSectors();
      refreshInventory();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Scout failed');
    }
  };

  const handleDeployDrone = async (droneId, resourceNodeId) => {
    if (!selectedSector) return;
    try {
      await api.deployDrone(droneId, selectedSector, resourceNodeId);
      setActionMsg('Drone deployed!');
      await loadSectors();
      loadDetail(selectedSector);
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Deploy failed');
    }
  };

  const handleRecallDrone = async (droneId) => {
    try {
      await api.recallDrone(droneId);
      setActionMsg('Drone recalled.');
      await loadSectors();
      if (selectedSector) loadDetail(selectedSector);
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Recall failed');
    }
  };

  const getSectorIcon = (type) => SECTOR_ICONS[type] || '◇';

  // Get non-zero resources from inventory for display
  // PostgreSQL BIGINT columns come back as strings — use Number() to handle both
  const getInventoryResources = () => {
    if (!sectorInv) return [];
    const skip = ['id', 'user_id', 'sector_id', 'createdAt', 'updatedAt', 'created_at', 'updated_at', 'credits'];
    return Object.entries(sectorInv)
      .filter(([k, v]) => !skip.includes(k) && Number(v) > 0)
      .map(([k, v]) => ({ key: k, amount: Number(v) }));
  };

  if (loading) return <div className="panel-loading">Loading sectors...</div>;

  return (
    <div className="panel-container sector-panel">
      <div className="panel-header">
        <h2>◇ Galaxy Sectors</h2>
      </div>

      {actionMsg && <div className="action-msg">{actionMsg}<button className="msg-dismiss" onClick={() => setActionMsg('')}>×</button></div>}

      <div className="sector-layout">
        {/* Sector List */}
        <div className="sector-list">
          <h3 className="section-title"><span className="title-accent">🗺</span> Discovered</h3>
          {sectors.length === 0 && <p className="hud-empty">No sectors discovered yet. Scout the undiscovered sectors below to begin exploring!</p>}
          {sectors.map(s => (
            <div
              key={s.id}
              className={`section sector-card ${selectedSector === s.id ? 'selected' : ''}`}
              onClick={() => loadDetail(s.id)}
            >
              <div className="sector-card-header">
                <span className="sector-icon">{getSectorIcon(s.type)}</span>
                <div className="sector-card-info">
                  <span className="sector-card-name">{s.name}</span>
                  <span className="sector-card-type">{s.type}</span>
                </div>
              </div>
              <div className="sector-card-meta">
                <span className="meta-item">⚒ {s.richness}x</span>
                <span className="meta-item">🏗 {s.building_slots} slots</span>
                {s.owner_id && <span className="badge status-active">OWNED</span>}
              </div>
            </div>
          ))}

          {undiscovered.length > 0 && (
            <>
              <h3 className="section-title mt-2"><span className="title-accent">?</span> Undiscovered</h3>
              {undiscovered.map(s => (
                <div key={s.id} className="section sector-card undiscovered">
                  <div className="sector-card-header">
                    <span className="sector-icon">❓</span>
                    <div className="sector-card-info">
                      <span className="sector-card-name">{s.name}</span>
                      <span className="sector-card-type">{s.type}</span>
                    </div>
                  </div>
                  <button className="btn btn-sm btn-glow" onClick={() => handleScout(s.id)}>🔭 Scout</button>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Sector Detail */}
        <div className="sector-detail">
          {sectorDetail ? (
            <>
              <div className="detail-header">
                <span className="sector-detail-icon">{getSectorIcon(sectorDetail.type)}</span>
                <div>
                  <h3>{sectorDetail.name}</h3>
                  <div className="detail-meta">
                    <span>{sectorDetail.type}</span>
                    <span>({sectorDetail.x}, {sectorDetail.y})</span>
                    <span>🏗 {sectorDetail.building_slots} slots</span>
                    <span>⚒ {sectorDetail.richness}x richness</span>
                  </div>
                </div>
              </div>

              {!sectorDetail.owner_id && (
                <button className="btn btn-glow" onClick={() => handleClaim(sectorDetail.id)}>
                  🚩 Claim Sector
                </button>
              )}

              {/* Resource Nodes */}
              {sectorDetail.resources && sectorDetail.resources.length > 0 && (
                <div className="detail-section">
                  <h4 className="section-title"><span className="title-accent">⛏</span> Resource Nodes</h4>
                  <div className="resource-grid">
                    {sectorDetail.resources.map(r => {
                      const pct = r.total_amount > 0 ? (r.remaining_amount / r.total_amount) * 100 : 0;
                      return (
                        <div key={r.id} className="resource-node">
                          <div className="resource-node-header">
                            <span className="resource-name">{fmtResource(r.resource_type)}</span>
                            <span className="resource-amount">
                              {Number(r.remaining_amount).toLocaleString()} / {Number(r.total_amount).toLocaleString()}
                            </span>
                          </div>
                          <div className="progress-track">
                            <div className="progress-fill" style={{ width: `${pct}%` }} />
                          </div>
                          {/* Deploy drone to this node */}
                          {drones.length > 0 && sectorDetail.owner_id && (
                            <div className="drone-deploy-row">
                              <select className="hud-select hud-select-sm" id={`deploy-${r.id}`} defaultValue="">
                                <option value="" disabled>Deploy drone...</option>
                                {drones.map(d => (
                                  <option key={d.id} value={d.id}>{d.name} (Lv.{d.level})</option>
                                ))}
                              </select>
                              <button className="btn btn-sm" onClick={() => {
                                const sel = document.getElementById(`deploy-${r.id}`);
                                if (sel.value) handleDeployDrone(Number(sel.value), r.id);
                              }}>Deploy</button>
                            </div>
                          )}
                          {/* Show deployed drones on this node */}
                          {r.drones && r.drones.length > 0 && (
                            <div className="deployed-drones">
                              {r.drones.map(d => (
                                <div key={d.id} className="deployed-drone-tag">
                                  <span>🤖 {d.name} (Lv.{d.level})</span>
                                  <button className="btn-unassign" onClick={() => handleRecallDrone(d.id)}>Recall</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Sector Storage — shows local resources for non-home sectors */}
              {(() => {
                const invResources = getInventoryResources();
                const isHome = sectorDetail.type === 'starter';
                if (isHome) return null; // home sector goes straight to cargo
                return (
                  <div className="detail-section">
                    <h4 className="section-title"><span className="title-accent">📦</span> Sector Storage <span className="hud-label" style={{fontWeight:'normal',opacity:0.6}}>(set up a transport route to move these home)</span></h4>
                    {invResources.length === 0
                      ? <p className="hud-empty" style={{fontSize:'0.8rem'}}>Empty — deploy drones to start mining. Resources accumulate here until transported.</p>
                      : (
                        <div className="inventory-grid compact">
                          {invResources.map(r => (
                            <div key={r.key} className="inv-item">
                              <span className="inv-name">{fmtResource(r.key)}</span>
                              <span className="inv-amount">{Number(r.amount).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )
                    }
                  </div>
                );
              })()}

              {/* Connected Sectors */}
              {sectorDetail.connections && sectorDetail.connections.length > 0 && (
                <div className="detail-section">
                  <h4 className="section-title"><span className="title-accent">🔗</span> Connections</h4>
                  {sectorDetail.connections.map(c => (
                    <div key={c.id} className="connection-item" onClick={() => {
                      const targetId = c.sector_a_id === sectorDetail.id ? c.sector_b_id : c.sector_a_id;
                      loadDetail(targetId);
                    }}>
                      <span className="connection-name">
                        {c.sectorA?.name || c.sectorB?.name || `Sector ${c.sector_a_id}/${c.sector_b_id}`}
                      </span>
                      <span className="connection-dist">📏 {c.distance} units</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="empty-detail">
              <span className="empty-icon">🗺</span>
              <p>Select a sector to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SectorPanel;
